import React, { useState, useContext, useEffect, useRef } from 'react';
import {
  Box, Text, Button, Grid, GridItem, VStack, Heading,
  useToast, Table, Thead, Tbody, Tr, Th, Td,
  Tag, Flex, Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter,
  ModalBody, ModalCloseButton, useDisclosure, Icon, Card, CardHeader, CardBody, Spinner,
  Input, FormControl, FormLabel
} from "@chakra-ui/react";
import { CheckCircleIcon, EditIcon, WarningIcon, ViewIcon } from '@chakra-ui/icons';

import { callApi } from '../callApi';
import { AppContext } from './AppContext';
import HeaderMenus from './HeaderMenus';

function PDFTemplateManager() {
  const { accessToken } = useContext(AppContext);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [headerTitle, setHeaderTitle] = useState('');
  const fileInputRef = useRef();

  const fetchTemplates = async () => {
    if (!accessToken) return;
    setIsLoading(true);
    try {
      const response = await callApi('/templates_with_pdf_details', accessToken);
      const data = await response.json();
      if (response.ok) {
        setTemplates(data);
      } else {
        throw new Error(data.error || 'Failed to fetch templates');
      }
    } catch (error) {
      toast({
        title: 'Error fetching templates',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [accessToken]);

  const handleOpenModal = (template) => {
    setSelectedTemplate(template);
    setSelectedFile(null);

    // Extract the current header title from the layout to pre-fill the input
    const headerSection = template.layout?.sections?.find(s => s.type === 'header');
    const textEntry = headerSection?.content?.find(c => c.type === 'text');
    setHeaderTitle(textEntry?.value || '');

    onOpen();
  };

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  const handlePreview = async (templateId) => {
    if (!accessToken) return;
    try {
      const response = await callApi(`/templates/${templateId}/preview_pdf`, accessToken);
      
      if (response.ok) {
        const blob = await response.blob();
        const fileURL = URL.createObjectURL(blob);
        window.open(fileURL, '_blank');
      } else {
        // Get detailed error from the backend response
        const errorText = await response.text();
        try {
          // Try to parse as JSON, which is the expected error format
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.error || `API Error: ${response.status}`);
        } catch (e) {
          // If it's not JSON, it's likely an HTML error page or plain text
          const shortError = errorText.substring(0, 300);
          throw new Error(`Server Error ${response.status}: ${shortError}...`);
        }
      }
    } catch (error) {
      toast({
        title: 'Error Generating Preview',
        description: error.message,
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    }
  };

  const handleSave = async () => {
    if (!selectedTemplate || !headerTitle) {
      toast({
        title: 'Validation Error',
        description: 'Header title is required.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('header_title', headerTitle);
    if (selectedFile) {
      formData.append('logo', selectedFile);
    }

    try {
      // Use the generic callApi and handle response manually
      const response = await callApi(`/templates/${selectedTemplate.id}/pdf`, accessToken, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      if (response.ok) {
        toast({
          title: 'Success',
          description: `PDF Header for ${selectedTemplate.name} updated successfully.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onClose();
        fetchTemplates(); // Refresh the list
      } else {
        throw new Error(data.error || 'Failed to update PDF template');
      }
    } catch (error) {
      toast({
        title: 'Update Failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    return new Date(isoString).toLocaleString();
  };

  return (
    <Box fontSize="xl">
      <Grid
        templateAreas={`"header header" "main main"`}
        gridTemplateRows={'60px 1fr'}
        gridTemplateColumns={'1fr'}
        h='100vh'
        gap='2'
      >
        <HeaderMenus />
        <GridItem bg='white' padding={4} area={'main'}>
          <VStack spacing={6} align="stretch">
            <Heading size="lg">PDF Template Configuration</Heading>
            <Text>Manage the logos and layouts for PDF note generation.</Text>
            <Card variant="outline">
              <CardHeader>
                <Heading size="md">Templates</Heading>
              </CardHeader>
              <CardBody>
                {isLoading ? (
                  <Flex justify="center" align="center" height="200px">
                    <Spinner size="xl" />
                  </Flex>
                ) : (
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Template ID</Th>
                        <Th>Template Name</Th>
                        <Th>Case Type</Th>
                        <Th>Active PDF Config</Th>
                        <Th>Logo Status</Th>
                        <Th>Last Updated</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {templates.map((template) => (
                        <Tr key={template.id}>
                          <Td>
                            <Text fontFamily="mono" fontSize="sm" color="blue.600" fontWeight="bold">
                              {template.id}
                            </Text>
                          </Td>
                          <Td fontWeight="bold">{template.name}</Td>
                          <Td>{template.case_type}</Td>
                          <Td>{template.pdf_template_name}</Td>
                          <Td>
                            {template.has_logo ? (
                              <Tag colorScheme="green" size="lg">
                                <Icon as={CheckCircleIcon} mr={2} />
                                Logo Present
                              </Tag>
                            ) : (
                              <Tag colorScheme="orange" size="lg">
                                <Icon as={WarningIcon} mr={2} />
                                No Logo
                              </Tag>
                            )}
                          </Td>
                          <Td>{formatDate(template.pdf_template_updated_at)}</Td>
                          <Td>
                            <Flex>
                              <Button
                                leftIcon={<EditIcon />}
                                colorScheme="teal"
                                variant="solid"
                                size="sm"
                                onClick={() => handleOpenModal(template)}
                                mr={2}
                              >
                                Update Logo
                              </Button>
                              <Button
                                leftIcon={<ViewIcon />}
                                colorScheme="blue"
                                variant="outline"
                                size="sm"
                                onClick={() => handlePreview(template.id)}
                              >
                                Preview
                              </Button>
                            </Flex>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </VStack>
        </GridItem>
      </Grid>

      {/* Update Header Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Update PDF Header for {selectedTemplate?.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={5}>
              <Text>
                Update the header title and optionally upload a new logo. This will create a new, active PDF configuration.
              </Text>
              
              <FormControl isRequired>
                <FormLabel>Header Title</FormLabel>
                <Input
                  value={headerTitle}
                  onChange={(e) => setHeaderTitle(e.target.value)}
                  placeholder="e.g., Athletic Trainer Note"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Logo Image (Optional)</FormLabel>
                <Input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/png, image/jpeg"
                  p={1}
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  If no file is chosen, the existing logo (if any) will be kept.
                </Text>
              </FormControl>

              {selectedFile && (
                <Text fontSize="sm" color="green.600">
                  New file selected: {selectedFile.name}
                </Text>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSave}
              isLoading={isUploading}
              isDisabled={!headerTitle}
            >
              Save and Activate
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default PDFTemplateManager;